import { defineComponent } from 'vue';
import LedStripDebug from './LedStripDebug.vue';
import LedStripDisplay from './LedStripDisplay.vue';
import SpiderWebDebug from './SpiderWebDebug.vue';
import LedDebug from './LedDebug.vue';
import GameStateChanger from './GameStateChanger.vue';
import { Card, Selector, Table, ButtonGroup } from '@/components/UI';
import useMobile from '@/compositions/useMobile';
import Grid from '../Room/RoomGrid/Grid.vue';
import TextInput from '@/components/UI/TextInput/TextInput.vue';
import LaserDebug from './LaserDebug.vue';
import { GroupState } from '@/types/group';
import DMXDebug from './DMXDebug.vue';
export default defineComponent({
    name: 'RoomDebug',
    components: {
        LedStripDebug,
        LedStripDisplay,
        LedDebug,
        LaserDebug,
        GameStateChanger,
        Card,
        Table,
        Selector,
        ButtonGroup,
        SpiderWebDebug,
        Grid,
        TextInput,
        DMXDebug
    },
    props: {
        sensors: {
            type: Array,
            required: true
        },
        gameState: {
            type: String,
            required: true
        },
        roomid: {
            type: String,
            required: true
        }
    },
    setup: () => ({
        ...useMobile()
    }),
    data: () => ({
        perpage: 215,
        text: '',
        fields: [
            { key: 'type', label: 'Type', sortable: true },
            { key: 'name', label: 'Name', sortable: true },
            { key: 'actions', label: 'Action', sortable: true }
        ],
        gameStatus: 'stopped',
        buttonOptions: [
            { value: 'released', text: 'released' },
            { value: 'clicked', text: 'clicked' }
        ],
        selectedRFID: '',
        status: null,
        config: null
    }),
    computed: {
        teams() {
            return this.$store.direct.state.teams.active;
        },
        level: {
            get() {
                var _a;
                return ((_a = this.status) === null || _a === void 0 ? void 0 : _a.level) ? this.status.level : 0;
            },
            set(levelnr) {
                this.updateLevel(levelnr);
            }
        },
        currentInput() {
            var _a, _b;
            return (_b = (_a = this.status) === null || _a === void 0 ? void 0 : _a.input) !== null && _b !== void 0 ? _b : '';
        },
        sensorlist() {
            const sensors = [];
            this.sensors.forEach(element => {
                let status = null;
                if (element.status === 'unknown' || !element.status) {
                    status = null;
                }
                else if (element.type == 'led' && typeof element.status === 'boolean') {
                    status = element.status ? 'on' : 'off';
                }
                else {
                    status = element.status;
                }
                const sensor = {
                    type: element.type,
                    name: element.name,
                    status: status,
                    note: this.findInfoOnButton(element.name)
                };
                sensors.push(sensor);
            });
            return sensors;
        },
        rfidOptions() {
            const options = [{ value: '', text: 'Scan team' }];
            Object.values(this.teams).forEach(team => {
                const member = team.members[0];
                if (member !== undefined)
                    options.push({ text: team.name, value: member.rfid });
            });
            Object.values(this.specialRFIDs).forEach(rfid => {
                options.push({ text: rfid.text, value: rfid.rfid });
            });
            return options;
        },
        specialRFIDs() {
            return this.$store.direct.state.rfid.specialRFID;
        },
        levelOptions() {
            const levels = [];
            for (let level = 0; level < 50; level++)
                levels.push({ value: level, text: `Level ${level + 1}` });
            return levels;
        },
        gridConfig() {
            return this.config;
        },
        gridStatus() {
            return this.status;
        },
        getLasers() {
            var _a;
            return ((_a = this.status) === null || _a === void 0 ? void 0 : _a.lasers) || [];
        },
        gridSensorStatus() {
            var _a, _b;
            return ((_b = (_a = this.status) === null || _a === void 0 ? void 0 : _a.sensors.find(sensor => sensor.name === 'grid')) === null || _b === void 0 ? void 0 : _b.status) || '';
        },
        getDMXChannels() {
            var _a;
            return ((_a = this.status) === null || _a === void 0 ? void 0 : _a.dmx_channels) || [];
        },
    },
    watch: {
        gameState(value) {
            this.gameStatus = value;
        }
    },
    mounted() {
        var _a, _b;
        this.gameStatus = this.gameState;
        this.$store.direct.dispatch.teams.subscribe(GroupState.Active);
        this.$store.direct.dispatch.rooms.subscribeStatus(this.roomid);
        this.$store.direct.dispatch.rooms.subscribe(this.roomid);
        this.status = (_a = this.$store.direct.state.rooms.status[this.roomid]) !== null && _a !== void 0 ? _a : null;
        this.config = (_b = this.$store.direct.state.rooms.config[this.roomid]) !== null && _b !== void 0 ? _b : null;
        this.$store.direct.dispatch.rfid.subscribe(undefined);
        this.text = this.currentInput;
    },
    destroyed() {
        this.$store.direct.dispatch.teams.unsubscribe(GroupState.Active);
        this.$store.direct.dispatch.rooms.unsubscribeStatus(this.roomid);
        this.$store.direct.dispatch.rooms.unsubscribe(this.roomid);
        this.$store.direct.dispatch.rfid.unsubscribe(undefined);
    },
    methods: {
        updateLed(params) {
            this.$emit('updateLed', params.name, params.value);
        },
        updateLaser(params) {
            this.$emit('updateLaser', params.name, params.value);
        },
        updateDMX(params) {
            this.$emit('updateDMX', params.name, params.value);
        },
        updateLedStrip(pattern, name) {
            this.$emit('updateLedStrip', name, pattern);
        },
        updateKeyboard() {
            console.log('updateKeyboard', this.text);
            if (this.text.length > 0) {
                for (const char of this.text) {
                    this.$store.direct.dispatch.rooms.setKeyboard({
                        roomid: this.roomid,
                        keyboard: 'codeinput',
                        value: char
                    });
                }
                this.text = '';
            }
        },
        updateLevel(level) {
            this.$store.direct.dispatch.rooms.setLevel({ roomid: this.roomid, level });
        },
        buttonChanged(source, event) {
            this.$emit('updateButton', source, event);
        },
        sendRFID(source) {
            this.$store.direct.dispatch.rooms.setRFID({ roomid: this.roomid, rfid: source, value: this.selectedRFID });
        },
        findInfoOnButton(id) {
            var _a;
            if (((_a = this.config) === null || _a === void 0 ? void 0 : _a.buttons) === undefined) {
                return '';
            }
            // @ts-ignore
            const note = this.config.buttons.find(button => button.id === id);
            if (note !== undefined && note.name !== undefined) {
                return note.name;
            }
            return '';
        },
        updateSpiderWeb(value, name) {
            console.log('updateSpiderWeb', name, value);
            this.$emit('updateSpiderWeb', name, value);
        }
    }
});
